import { Injectable } from '@angular/core';

/**
 * Business context for log enrichment.
 * All logs will be automatically enriched with this context when set.
 */
export interface LogContext {
  /** Payment link identifier */
  paymentLinkId?: string;

  /** Establishment (merchant) identifier */
  establishmentId?: number;

  /** Payment link type (0=Standard, 1=Checkout, 2=GRERJ) */
  linkType?: number;

  /** Currency code (EUR, USD, BRL) */
  currency?: string;

  /** Current operation name for grouping related logs */
  operationName?: string;

  /** Additional custom properties */
  [key: string]: unknown;
}

/**
 * Service for managing business context that enriches log entries.
 *
 * Set context when entering a payment flow to automatically include
 * business identifiers (paymentLinkId, establishmentId) in all subsequent logs.
 *
 * @example
 * ```typescript
 * // In payment page component
 * ngOnInit() {
 *   this._logContext.setContext({
 *     paymentLinkId: this.paymentLink.id,
 *     establishmentId: this.establishment.id,
 *     linkType: this.paymentLink.linkType,
 *     currency: this.paymentLink.currencyCode
 *   });
 * }
 *
 * ngOnDestroy() {
 *   this._logContext.clear();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class LogContextService {
  private _context: LogContext = {};

  /**
   * Set or update the current business context.
   * Merges with existing context (does not replace).
   *
   * @param context Partial context to set or update
   */
  setContext(context: Partial<LogContext>): void {
    this._context = { ...this._context, ...context };
  }

  /**
   * Get the current business context.
   * Returns a shallow copy to prevent external mutation.
   *
   * @returns Current log context
   */
  getContext(): LogContext {
    return { ...this._context };
  }

  /**
   * Set a specific operation name in the context.
   * Useful for grouping related logs within a business operation.
   *
   * @param operationName Name of the current operation
   */
  setOperationName(operationName: string): void {
    this._context.operationName = operationName;
  }

  /**
   * Clear the current operation name while preserving other context.
   */
  clearOperationName(): void {
    delete this._context.operationName;
  }

  /**
   * Clear all business context.
   * Call this when leaving a payment flow or resetting state.
   */
  clear(): void {
    this._context = {};
  }

  /**
   * Check if a context has been set.
   *
   * @returns true if context has at least one property set
   */
  hasContext(): boolean {
    return Object.keys(this._context).length > 0;
  }
}
